import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Target, Lightbulb, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startRecommendationFlow } from "@/lib/cta";

// Mobile-only sticky bottom bar on the HOME page only. Three equal, compact
// CTAs whose labels fill the full width of each segment: Business Fit,
// Business Ideas, Business Partners.
export default function HomeMobileCTABar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(() => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch { return false; }
  });

  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia("(max-width: 767px)");
      const h = (e) => setIsMobile(e.matches);
      mq.addEventListener("change", h);
      return () => mq.removeEventListener("change", h);
    } catch { /* noop */ }
  }, []);

  const active = pathname === "/" && isMobile;

  // Reserve space so the fixed bar never covers the footer / page content.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "56px";
    return () => { document.body.style.paddingBottom = prev; };
  }, [active]);

  if (!active) return null;

  const items = [
    {
      key: "fit", label: "Business Fit", Icon: Target,
      testid: "home-cta-fit",
      onClick: () => startRecommendationFlow({ user, nav: navigate }),
      className: "bg-emerald-600 text-white hover:bg-emerald-700",
    },
    {
      key: "ideas", label: "Business Ideas", Icon: Lightbulb,
      testid: "home-cta-ideas",
      onClick: () => navigate("/business-ideas"),
      className: "bg-white text-emerald-800 hover:bg-emerald-50",
    },
    {
      key: "partners", label: "Business Partners", Icon: Users,
      testid: "home-cta-partners",
      onClick: () => navigate("/business-partners"),
      className: "bg-slate-900 text-white hover:bg-slate-800",
    },
  ];

  return (
    <div
      data-testid="home-cta-bar"
      className="fixed inset-x-0 bottom-0 z-50 flex border-t border-emerald-200 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:hidden"
    >
      {items.map(({ key, label, Icon, testid, onClick, className }) => (
        <button
          key={key}
          onClick={onClick}
          data-testid={testid}
          className={`flex-1 flex items-center justify-center gap-1.5 px-1 py-3 text-[13px] font-bold leading-none transition-colors ${className}`}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="whitespace-nowrap">{label}</span>
        </button>
      ))}
    </div>
  );
}
